import React from "react";
import {
  Box,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Container,
} from "@mui/material";
import { motion } from "framer-motion";

const orange = "#ff6600";
const dark = "#000000";
const grayText = "#444444";
const grayTitle = "#333333";

const items = [
  { title: "Diagnóstico inicial gratuito", desc: "Analizamos tu volumen de impresión, equipos actuales y necesidades reales." },
  { title: "Equipos de última tecnología", desc: "Instalamos impresoras y multifuncionales modernas y eficientes." },
  { title: "Suministro continuo de insumos", desc: "Tóner, tintas y repuestos incluidos sin que tengas que preocuparte." },
  { title: "Mantenimiento preventivo", desc: "Nuestro equipo técnico se encarga de todo para evitar interrupciones." },
  { title: "Soporte técnico prioritario", desc: "Atención rápida y especializada cuando lo necesites." },
  { title: "Control y monitoreo", desc: "Optimiza el uso de recursos con reportes y control de consumo." },
];

const benefits = [
  "Reducción de hasta un 40% en costos",
  "Eliminación de gastos imprevistos",
  "Mayor productividad del personal",
  "Equipos siempre operativos",
  "Control total del gasto",
  "Solución escalable",
];

const idealFor = [
  "Oficinas administrativas",
  "Empresas comerciales",
  "Instituciones educativas",
  "Estudios contables y jurídicos",
  "Centros médicos",
  "Negocios en expansión",
];

const PrintUX = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const firstHalf = items.slice(0, 3);
  const secondHalf = items.slice(3, 6);

  // Tarjeta ajustada para estirarse al 100% de su contenedor
  const ServiceCard = ({ item }) => (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center", // Centra el contenido verticalmente
        p: 2.5,
        borderRadius: "16px",
        backgroundColor: "#fafafa",
        border: "1px solid #eeeeee",
        textAlign: "center",
        transition: "all 0.3s ease",
        "&:hover": {
          backgroundColor: "#fff7f1",
          borderColor: "#ffe6d5",
          transform: "translateY(-3px)",
          boxShadow: "0 6px 15px rgba(0,0,0,0.05)",
        },
      }}
    >
      <Typography
        component="h4"
        sx={{
          fontSize: { xs: "1rem", md: "1.12rem" },
          fontWeight: "bold",
          color: dark,
          mb: 1,
        }}
      >
        {item.title}
      </Typography>
      <Typography
        sx={{
          fontSize: { xs: "0.92rem", md: "0.98rem" },
          color: grayText,
          lineHeight: 1.5,
        }}
      >
        {item.desc}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: "#fff", py: { xs: 4, md: 7 }, px: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg">
        {/* Cabecera */}
        <Box sx={{ textAlign: "center", mb: 5 }}>
          <motion.div initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Typography component="h2" sx={{ fontSize: { xs: "1.5rem", md: "2.3rem" }, fontWeight: "bold", mb: 1.5, color: grayTitle }}>
              Outsourcing de Impresión Empresarial
            </Typography>
            <Typography sx={{ fontSize: { xs: "0.95rem", md: "1.1rem" }, color: grayText, maxWidth: "900px", mx: "auto", lineHeight: 1.5, textAlign: "center" }}>
              En <strong style={{ color: orange }}>CMSOLTEC</strong> optimizamos la gestión de impresión de tu empresa para que te concentres en lo que realmente importa. Nuestro servicio reduce costos y elimina los dolores de cabeza operativos.
            </Typography>
          </motion.div>
        </Box>

        {/* Sección Central alineada simétricamente */}
        <Grid container spacing={3} alignItems="stretch" justifyContent="center">
          
          {/* Lado Izquierdo */}
          <Grid item xs={12} md={3.5} order={{ xs: 2, md: 1 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {firstHalf.map((item, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, x: -20 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                transition={{ delay: i * 0.1 }}
                style={{ flex: 1, display: "flex" }} // flex: 1 fuerza a que todos tengan la misma altura
              >
                <ServiceCard item={item} />
              </motion.div>
            ))}
          </Grid>

          {/* Imagen Central */}
          <Grid item xs={12} md={5} order={{ xs: 1, md: 2 }} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: { xs: 3, md: 0 } }}>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
              <Box sx={{ borderRadius: "20px", overflow: "hidden", boxShadow: "0 15px 35px rgba(0,0,0,0.08)", maxWidth: { xs: "280px", md: "100%" } }}>
                <img 
                  src={require("../../static/media/service/service5.webp")} 
                  alt="Servicio Impresión" 
                  style={{ width: "100%", height: "auto", display: "block" }} 
                />
              </Box>
            </motion.div>
          </Grid>

          {/* Lado Derecho */}
          <Grid item xs={12} md={3.5} order={{ xs: 3, md: 3 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {secondHalf.map((item, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, x: 20 }} 
                whileInView={{ opacity: 1, x: 0 }} 
                transition={{ delay: i * 0.1 }}
                style={{ flex: 1, display: "flex" }} // flex: 1 fuerza a que todos tengan la misma altura
              >
                <ServiceCard item={item} />
              </motion.div>
            ))}
          </Grid>

        </Grid>

        {/* Beneficios e Ideal Para */}
        <Grid container spacing={3} sx={{ mt: 4 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "18px", backgroundColor: "#fff7f1", height: "100%", border: "1px solid #ffe6d5" }}>
              <Typography component="h3" sx={{ fontSize: "1.25rem", fontWeight: "bold", mb: 2, color: dark }}>💰 Beneficios</Typography>
              <Grid container spacing={1.5}>
                {benefits.map((text, i) => (
                  <Grid item xs={12} sm={6} md={12} key={i}>
                    <Typography sx={{ fontSize: "0.95rem", color: grayText }}>🔹 {text}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: { xs: 3, md: 4 }, borderRadius: "18px", backgroundColor: "#f9f9f9", height: "100%", border: "1px solid #eee" }}>
              <Typography component="h3" sx={{ fontSize: "1.25rem", fontWeight: "bold", mb: 2, color: dark }}>🏢 Ideal para</Typography>
              <Grid container spacing={1.5}>
                {idealFor.map((text, i) => (
                  <Grid item xs={12} sm={6} md={12} key={i}>
                    <Typography sx={{ fontSize: "0.95rem", color: grayText }}>• {text}</Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* Footer info */}
        <Box sx={{ mt: 6, p: { xs: 3, md: 5 }, borderRadius: "20px", background: "linear-gradient(135deg, #fff 0%, #fff9f5 100%)", border: "1px solid #f0f0f0", textAlign: 'center' }}>
          <Typography component="h3" sx={{ fontSize: "1.4rem", fontWeight: "bold", mb: 2, color: dark }}>¿Por qué elegir CMSOLTEC?</Typography>
          <Typography sx={{ fontSize: "1rem", color: grayText, lineHeight: 1.7, maxWidth: "900px", mx: "auto" }}>
            Combinamos experiencia técnica y atención personalizada para ofrecerte una solución integral. Aseguramos que tu infraestructura de impresión funcione de manera eficiente, segura y sin interrupciones, permitiéndote un ahorro real desde el primer mes.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrintUX;